import { useState } from 'react';
import { Modal } from '../ui';
import { LoginForm } from './LoginForm';
import { RegisterForm } from './RegisterForm';
import { ConfirmSignUpForm } from './ConfirmSignUpForm';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { ResetPasswordForm } from './ResetPasswordForm';

type AuthView =
  | 'login'
  | 'register'
  | 'confirmSignUp'
  | 'forgotPassword'
  | 'resetPassword';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialView?: AuthView;
}

const viewTitles: Record<AuthView, string> = {
  login: 'Welcome Back',
  register: 'Create Account',
  confirmSignUp: 'Verify Your Email',
  forgotPassword: 'Reset Password',
  resetPassword: 'Set New Password',
};

const viewDescriptions: Record<AuthView, string> = {
  login: 'Sign in to continue playing',
  register: 'Join thousands of gamers',
  confirmSignUp: 'Almost there!',
  forgotPassword: "We'll help you get back in",
  resetPassword: 'Choose a strong password',
};

export function AuthModal({
  isOpen,
  onClose,
  initialView = 'login',
}: AuthModalProps) {
  const [view, setView] = useState<AuthView>(initialView);
  const [email, setEmail] = useState('');

  const handleLoginSuccess = () => {
    onClose();
  };

  const handleRegisterSuccess = (registeredEmail: string) => {
    setEmail(registeredEmail);
    setView('confirmSignUp');
  };

  const handleConfirmSuccess = () => {
    setView('login');
  };

  const handleForgotPasswordSuccess = (resetEmail: string) => {
    setEmail(resetEmail);
    setView('resetPassword');
  };

  const handleResetPasswordSuccess = () => {
    setView('login');
  };

  const renderContent = () => {
    switch (view) {
      case 'login':
        return (
          <LoginForm
            onSuccess={handleLoginSuccess}
            onSwitchToRegister={() => setView('register')}
            onForgotPassword={() => setView('forgotPassword')}
          />
        );

      case 'register':
        return (
          <RegisterForm
            onSuccess={handleRegisterSuccess}
            onSwitchToLogin={() => setView('login')}
          />
        );

      case 'confirmSignUp':
        return (
          <ConfirmSignUpForm
            email={email}
            onSuccess={handleConfirmSuccess}
            onBack={() => setView('register')}
          />
        );

      case 'forgotPassword':
        return (
          <ForgotPasswordForm
            onSuccess={handleForgotPasswordSuccess}
            onBack={() => setView('login')}
          />
        );

      case 'resetPassword':
        return (
          <ResetPasswordForm
            email={email}
            onSuccess={handleResetPasswordSuccess}
            onBack={() => setView('forgotPassword')}
          />
        );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={viewTitles[view]}
      description={viewDescriptions[view]}
      size="sm"
    >
      {renderContent()}
    </Modal>
  );
}
